/**
 * ## `api.ts` 接口 URL 集中管理（平铺结构）
 * baseURL: /api/gtrip/console（已在 request.ts 中配置）
 * 此处只写相对路径
 *
 * 使用方式：
 *   import Api from '@/http/api';
 *   Api.GET_LOGIN
 *   Api.POST_COMMON_TICKET_TO_OPEN
 */

// ==================== 认证模块 ====================
const auth = {
  /** 获取验证码 */
  GET_VERIFY_CODE: "/facade/sale/login/verify/code/query",
  /** 获取用户机构 */
  GET_USER_STORE: "/facade/sale/user/getStoreByAccount",
  /** 登录接口 */
  GET_LOGIN: "/facade/sale/login/in",
  /** 获取未提交预审单是否超时 */
  GET_PRE_TRIAL_TIMEOUT: "/facade/sale/preTrial/preTrialTimeoutReminder",
  /** 获取用户资产信息 */
  GET_USER_ASSET: "/facade/sale/agency/user/asset",
} as const;

// ==================== 支付模块 ====================
const payment = {
  /** 门市、代理收款方式 */
  DEPOSIT_TRANSACTION_MODE_LIST: "/facade/sale/deposit/transaction/mode/list",
  /** 获取POS机列表 */
  GET_POS_LIST: "/facade/sale/posTerminal/by/account",
} as const;

// ==================== 订单模块 ====================
const order = {
  /** 消位接口 */
  POST_COMMON_TICKET_TO_OPEN: "/facade/sale/open/ticket/commonTicketToOpen",
  /** 消位预检查接口 */
  POST_COMMON_TICKET_TO_OPEN_PRE:
    "/facade/sale/open/ticket/commonTicketToOpenPre",
  /** 获取打印收据 */
  GET_RECEIPT: "/facade/sale/tour/product/receipt/info",
  /** 补价票下单 */
  POST_PREMIUM_TICKET_ORDER: "/facade/sale/agency/order/premium/take",
  /** 行李票后台退票预校验 */
  POST_BAGGAGE_REFUND_PRE_VERIFY:
    "/facade/sale/ticket/baggage/backend/refund/pre/verify",
  /** 行李票后台退票确认 */
  POST_BAGGAGE_REFUND_CONFIRM:
    "/facade/sale/ticket/baggage/backend/refund/confirm",
  /** 包车票后台退票确认 */
  POST_REFUND_CONFIRM: "/facade/sale/chartered/backend/refund/confirm",
  /** 获取Open票价格规则（包含标准价和营销价） */
  GET_OPEN_PRICE_WITH_MARKET: "/facade/sale/agency/open/price/queryWithMarket",
  /** Open票下单 */
  POST_OPEN_ORDER_TAKE: "/facade/sale/agency/open/order/take",
  /** 获取选座班次座位详情 */
  POST_SEAT_DETAIL: "/facade/sale/line/choose/seat/detail",
  /** 获取班次票价列表 */
  GET_LINE_PRICES_FOR_DISPLAY:
    "/facade/sale/agency/lines/getLinePricesForDisplay",
  /** 将返程改为Open */
  POST_CHANGE_TO_OPEN: "/facade/sale/agency/line/seat/changeToOpen",
} as const;

// ==================== 旅游产品模块 ====================
const tourProduct = {
  /** 获取旅游产品类型 */
  GET_TICKETS_TYPES: "/console/tp/api/tickets/types",
  /** 获取打印门票 */
  GET_APITICKET: "/facade/sale/tour/product/api/ticket/info",
  /** 修改旅游产品订单供应商订单号 */
  POST_PRODUCT_ORDER_INFO_UPDATE: "/facade/sale/tour/product/order/info/update",
  /** 获取取卡串码 */
  GET_PRODUCT_PRINT_CARD_NO: "/facade/sale/tour/product/print/card/no/query",
  /** 获取旅游产品收据-加密order */
  GET_Tour_RECEIPT: "/facade/sale/order/tour/product/receipt/info",
  /** 获取旅游产品教练短信-加密order */
  GET_Tour_COACH_MESSAGE: "/facade/sale/order/tour/product/simpleInfo",
} as const;

// ==================== 报表模块 ====================
const report = {
  /** 获取报表供应商旅游产品类别 */
  GET_REPORT_SUPPLIER_TOUR_PRODUCT_CATEGORY:
    "/console/tour/product/queryCategoryByGrade",
  /** 获取报表供应商旅游产品供应商列表 */
  GET_REPORT_SUPPLIER_TOUR_PRODUCT_SUPPLIER_LIST: "/console/supplier/page",
  /** 获取报表供应商旅游产品类型 */
  POST_REPORT_SUPPLIER_TOUR_PRODUCT_TYPE:
    "/facade/sale/tour/product/type/queryTourProductTypeList",
  /** 获取报表供应商旅游产品商品列表 */
  POST_REPORT_SUPPLIER_TOUR_PRODUCT_LIST:
    "/facade/sale/tour/product/list/queryProductByTypes",
  /** 获取取位留位报表路线列表 */
  GET_SEAT_HOLD_LINE_LIST: "/console/route/short/list/withPermission",
  /** 获取万能销售报表表头列表 */
  GET_UNIVERSAL_SALE_REPORT_HEADER_LIST: "/forms/report/generic/sell/params",
  /** 获取万能取位报表表头列表 */
  GET_UNIVERSAL_HOLD_REPORT_HEADER_LIST:
    "/forms/report/generic/confirm/seat/params",
  /** 获取任务列表 */
  POST_TASK_MANAGE_PAGE: "/forms/schedule/manage/page",
  /** 取消任务 */
  POST_TASK_CANCEL: "/forms/schedule/cancel",
  /** 恢复任务 */
  POST_TASK_RECOVER: "/forms/schedule/recover",
  /** 设置任务最高优先级 */
  POST_TASK_PIN_TO_TOP: "/forms/schedule/pinToTop",
  /** 删除任务 */
  POST_TASK_DELETE: "/forms/schedule/delete",
  /** 旅游销售产品详情 */
  GET_TOUR_PRODUCT_DETAILS:
    "/facade/sale/tour/product/pack/product/price/stations/query",
  /** API套票路线列表（按 packTypeCode 筛选） */
  GET_PACK_ROUTE_SHORT_LIST: "/facade/sale/tour/product/pack/route/short/list",
  /** API套票产品列表（按 packTypeCode 筛选） */
  GET_PACK_PRODUCTS: "/facade/sale/tour/product/pack/products/query",
  /** 西九文化区-查询子分类 */
  GET_WESTK_CATEGORY: "/facade/sale/tour/product/at/westk/category/query",
  /** 西九文化区-查询门票列表（单售） */
  GET_WESTK_PRODUCTS: "/facade/sale/tour/product/at/westk/products/query",
  /** 西九文化区-门票下单 */
  POST_WESTK_TAKE_ORDER: "/facade/sale/tour/product/at/westk/take/order",
  /** 西九文化区-关闭订单 */
  POST_WESTK_CLOSE_ORDER: "/facade/sale/tour/product/at/westk/close/order",
  /** 旅游产品套票-关闭订单 */
  POST_TOUR_PACK_CLOSE_ORDER: "/facade/sale/tour/product/pack/close/order",
  /** 西九文化区-后台查询可导入产品 */
  GET_WESTK_IMPORT_PRODUCTS: "/console/tp/api/tickets/westk/product/query",
  /** 西九文化区-后台确认导入产品 */
  POST_WESTK_IMPORT_CONFIRM: "/console/tp/api/tickets/westk/product/confirm",
} as const;

// ==================== 线路模块 ====================
const line = {
  /** 查询路线出发城市区域列表 */
  GET_DEPARTURE_ROUTE_CITY_AREA_LIST:
    "/console/route/getDepartureRouteCityAreaList",
  /** 获取运营公司列表 */
  POST_OPERATE_COMPANY_LIST: "/console/operate/company/listAll",
  /** 获取所有关口站点 */
  GET_ALL_GATEWAY_STATION: "/console/line/station/getTransferStations",
} as const;

// ==================== 代理模块 ====================
const agent = {
  /** 价格政策代理列表 */
  POST_PRICE_POLICY_AGENCY_LIST: "/console/priceMarket/queryPageStoreAndAgency",
} as const;

// ==================== 共享模块 ====================
const common = {
  /** 获取城市区域站点信息 */
  GET_CITY_AREA_STATIONS: "/facade/sale/base/city/area/stations",
} as const;

// ==================== 系统模块 ====================
const system = {
  /** 发送短信 */
  POST_SEND_SMS: "/facade/sale/message/sendMessage",
  /** 短信详情 */
  GET_MESSAGE_DETAIL: "/facade/sale/message/detail",
  /** 重发信息 */
  POST_MESSAGE_RESEND: "/facade/sale/message/resendRecordMessage",
  /** 上报打印日志 */
  POST_LOGFORFRONT: "/facade/sale/user/logForFront",
} as const;

const Api = {
  ...auth,
  ...payment,
  ...order,
  ...tourProduct,
  ...report,
  ...line,
  ...agent,
  ...common,
  ...system,
} as const;

// 开发环境检查 URL 是否重复（fail-fast：有重复则直接阻断页面加载）
if (process.env.NODE_ENV === "development") {
  const urlMap = new Map<string, string[]>();
  for (const [key, url] of Object.entries(Api)) {
    const keys = urlMap.get(url);
    if (keys) {
      keys.push(key);
    } else {
      urlMap.set(url, [key]);
    }
  }
  const duplicates = [...urlMap].filter(([, keys]) => keys.length > 1);
  if (duplicates.length > 0) {
    throw new Error(
      `[API 重复] 发现 ${duplicates.length} 个重复 URL:\n` +
        duplicates
          .map(([url, keys]) => `  ${url} → ${keys.join(", ")}`)
          .join("\n")
    );
  }
}

export default Api;
